import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListDerivedFieldsResult } from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  Field,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The expression editor (F-011).
 *
 * Three things beside the box, because writing a formula is three separate
 * difficulties: which functions exist, what the fields are called, and whether
 * the thing you just wrote produces what you expected. The preview runs
 * against a REAL record rather than an invented one - the mistakes this editor
 * catches are about the shape of actual data, and made-up values have none of
 * them.
 */

export function FormulaPanel({
  objects,
}: {
  objects: Array<{ key: string; pluralLabel: string }>;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const entitlement = useEntitlement('F-011');
  const [objectKey, setObjectKey] = useState(objects[0]?.key ?? 'deal');
  const [open, setOpen] = useState(false);
  const [fieldKey, setFieldKey] = useState('');
  const [label, setLabel] = useState('');
  const [formula, setFormula] = useState('');

  const derived = useQuery<ListDerivedFieldsResult, ApiError>({
    queryKey: ['derived-fields', objectKey],
    queryFn: () => api.listDerivedFields({ path: { objectKey } }),
    enabled: entitlement.state === 'on',
  });

  const layout = useQuery({
    queryKey: ['object-layout', objectKey],
    queryFn: () => api.getObjectLayout({ path: { objectKey } }),
    enabled: entitlement.state === 'on',
  });

  /*
   * The preview re-runs as the expression changes, and writes nothing - the
   * operation is declared `read` for exactly that reason.
   */
  const preview = useQuery({
    queryKey: ['formula-preview', objectKey, formula],
    queryFn: () => api.previewFormula({ path: { objectKey }, body: { formula, recordId: null } }),
    enabled: entitlement.state === 'on' && open && formula.trim() !== '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.createDerivedField({
        path: { objectKey },
        body: { fieldKey, label, type: 'formula', formula },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['derived-fields', objectKey] });
      setOpen(false);
      setFieldKey('');
      setLabel('');
      setFormula('');
    },
  });

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="formulas" id="formulas">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('formula.title')}</SectionLabel>
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('formula.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('formula.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (derived.isPending) return <Skeleton className="h-block-md w-full" />;
  if (derived.isError) {
    return (
      <ErrorState title={t('state.error')} body={derived.error.message} onRetry={() => void derived.refetch()} />
    );
  }

  return (
    <Panel data-region="formulas" id="formulas">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>{t('formula.title')}</SectionLabel>
          <label className="ms-auto flex items-center gap-2 text-dense text-ink-600">
            {t('fls.object')}
            <select
              data-formula-object
              value={objectKey}
              onChange={(event) => setObjectKey(event.currentTarget.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              {objects.map((object) => (
                <option key={object.key} value={object.key}>
                  {object.pluralLabel}
                </option>
              ))}
            </select>
          </label>
          <Button
            intent="secondary"
            size="small"
            icon="Plus"
            data-new-formula
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            {t('formula.new')}
          </Button>
        </div>
        <p className="text-meta text-ink-meta">{t('formula.body')}</p>

        {derived.data.fields.length === 0 ? (
          <p className="text-dense text-ink-meta">{t('formula.empty')}</p>
        ) : (
          <ul className="flex flex-col">
            {derived.data.fields.map((field) => (
              <li
                key={field.fieldKey}
                data-derived-field={field.fieldKey}
                className="flex flex-wrap items-baseline gap-3 border-t border-t-rule py-2 first:border-t-0"
              >
                <span className="text-body text-ink">{field.label}</span>
                <span className="rounded-chip bg-sunken px-1.5 text-meta text-ink-600">
                  {t(`formula.kind.${field.type}` as never)}
                </span>
                <code className="ms-auto truncate font-mono text-dense text-ink-meta">
                  {field.formula ??
                    `${field.rollup?.fn ?? ''} ${field.rollup?.field ?? ''} · ${field.rollup?.objectKey ?? ''}`}
                </code>
              </li>
            ))}
          </ul>
        )}

        {!open ? null : (
          <div data-formula-editor className="flex flex-col gap-3 rounded-panel border border-rule p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field
                label={t('formula.name')}
                data-formula-key
                value={fieldKey}
                onChange={(event) => setFieldKey(event.currentTarget.value)}
              />
              <Field
                label={t('formula.label')}
                data-formula-label
                value={label}
                onChange={(event) => setLabel(event.currentTarget.value)}
              />
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('formula.expression')}
              </span>
              <textarea
                data-formula-expression
                value={formula}
                onChange={(event) => setFormula(event.currentTarget.value)}
                rows={3}
                className="w-full rounded-control border border-control bg-surface p-2 font-mono text-dense text-ink"
              />
            </label>

            {/* Functions, from the evaluator itself, so the editor cannot
                offer one the engine does not have. */}
            <div className="flex flex-col gap-1">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('formula.functions')}
              </span>
              <div className="flex flex-wrap gap-1">
                {derived.data.functions.map((fn) => (
                  <button
                    key={fn.name}
                    type="button"
                    data-formula-function={fn.name}
                    title={fn.hint}
                    onClick={() => setFormula((current) => `${current}${fn.name}(`)}
                    className="rounded-chip bg-sunken px-2 py-0.5 font-mono text-meta text-ink-600 hover:bg-bronze-tint hover:text-bronze-text"
                  >
                    {fn.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-micro uppercase tracking-label text-ink-label">{t('formula.fields')}</span>
              <div className="flex flex-wrap gap-1">
                {(layout.data?.fields ?? []).slice(0, 24).map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    data-formula-field={field.key}
                    onClick={() => setFormula((current) => `${current}${field.key}`)}
                    className="rounded-chip bg-sunken px-2 py-0.5 font-mono text-meta text-ink-600 hover:bg-bronze-tint hover:text-bronze-text"
                  >
                    {field.key}
                  </button>
                ))}
              </div>
            </div>

            {/* The answer, against a real record. */}
            <div data-formula-preview className="flex flex-col gap-1 rounded-card bg-sunken p-3">
              <span className="text-micro uppercase tracking-label text-ink-label">{t('formula.preview')}</span>
              {formula.trim() === '' ? null : preview.isPending ? (
                <Skeleton className="h-5 w-block-sm" />
              ) : preview.data?.error ? (
                <p data-formula-error className="text-dense text-danger-text">
                  {preview.data.error.message}
                </p>
              ) : preview.data?.sampleId === null ? (
                <p className="text-dense text-ink-meta">{t('formula.preview.none')}</p>
              ) : (
                <Num data-formula-value className="text-body text-ink">
                  {String(preview.data?.value ?? '')}
                </Num>
              )}
            </div>

            {create.isError ? (
              <p data-formula-refusal className="text-dense text-danger-text">
                {create.error.message}
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button
                intent="primary"
                size="small"
                data-save-formula
                busy={create.isPending}
                disabled={fieldKey.trim() === '' || formula.trim() === ''}
                onClick={() => create.mutate()}
              >
                {t('formula.save')}
              </Button>
              <Button intent="ghost" size="small" onClick={() => setOpen(false)}>
                {t('permissions.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
